import { Link } from "react-router-dom";
import { ShieldCheck, Atom, Timer, ArrowRight, AlertTriangle, CheckCircle2, XCircle, Lock, HeartPulse, Microscope, Quote } from "lucide-react";
import { BookDemoButton } from "@/components/BookDemo";

const IMG = (f) => `/case-study/${f}`;

const COMPARISON = [
    { prop: "Molecular bonding", metal: "—", nano: "Double bonding of molecules — dual covalent coupling at the matrix–filler interface" },
    { prop: "Biocompatibility", metal: "Potential for corrosion and allergic reactions (e.g. nickel)", nano: "Excellent — fully metal-free, safe for immunocompromised patients" },
    { prop: "Precision / marginal fit", metal: "Hard to achieve consistently — casting shrinkage and distortion", nano: "High precision, excellent marginal adaptation from the digital design file" },
    { prop: "Aesthetics", metal: "Metallic appearance, less natural", nano: "Mimics natural tooth translucency and colour stability" },
    { prop: "Strength", metal: "Generally high", nano: "High strength and durability — engineered for high bite force" },
    { prop: "Corrosion resistance", metal: "Variable, depends on alloy", nano: "Excellent — no metal ions released" },
    { prop: "Allergenicity", metal: "Potential for metal allergies", nano: "Low risk of allergic reactions" },
];

const Figure = ({ src, caption, tall = false }) => (
    <figure className="rounded-2xl overflow-hidden border border-slate-200 bg-white">
        <a href={src} target="_blank" rel="noreferrer">
            <img src={src} alt={caption} loading="lazy" className={`w-full object-cover ${tall ? "h-72" : "h-56"} hover:opacity-90 transition`} />
        </a>
        <figcaption className="px-4 py-3 text-[12px] text-slate-500 leading-snug">{caption}</figcaption>
    </figure>
);

const Blockquote = ({ children, by }) => (
    <blockquote className="my-6 rounded-2xl bg-rose-50 border-l-4 border-rose-400 px-6 py-5">
        <Quote size={16} className="text-rose-400 mb-2" />
        <p className="text-[15.5px] text-slate-800 italic leading-relaxed">{children}</p>
        <div className="mt-2 text-[12px] text-slate-500">{by}</div>
    </blockquote>
);

const Chapter = ({ eyebrow, title, children }) => (
    <section className="max-w-4xl mx-auto px-6 lg:px-10 py-14">
        <div className="font-mono-tr text-[10.5px] tracking-[0.22em] uppercase text-cyan-700">{eyebrow}</div>
        <h2 className="mt-2 font-display text-3xl lg:text-4xl tracking-tight leading-[1.15]">{title}</h2>
        {children}
    </section>
);

export default function CaseStudyPatientX() {
    return (
        <div className="bg-white text-slate-900" data-testid="case-study-patient-x">
            {/* HERO */}
            <section className="relative bg-[#0B1120] text-white overflow-hidden tr-grain">
                <div className="absolute -top-32 -right-20 w-[460px] h-[460px] tr-glow bg-cyan-500/30" />
                <div className="absolute bottom-0 -left-32 w-[380px] h-[380px] tr-glow bg-teal-500/20" />
                <div className="max-w-4xl mx-auto px-6 lg:px-10 py-20 lg:py-28 relative">
                    <Link to="/case-studies" data-testid="cs-back-to-index" className="inline-flex items-center gap-1.5 text-[12.5px] text-cyan-300/80 hover:text-cyan-200 mb-6 font-mono-tr tracking-[0.14em] uppercase">
                        ← All case studies
                    </Link>
                    <div className="font-mono-tr text-[10.5px] tracking-[0.28em] uppercase text-cyan-300/80">· Case study · Oncology · Nano-hybrid ceramic</div>
                    <h1 className="mt-4 font-display font-light text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.08]">
                        Two failed metal onlays.<br />
                        A cancer treatment on hold.<br />
                        <span className="text-cyan-300">One precise restoration that changed everything.</span>
                    </h1>
                    <p className="mt-6 text-white/70 max-w-2xl text-lg leading-[1.7]">
                        How Toothrocket's nano-hybrid ceramic 3D printing — with <strong className="text-white">double bonding of molecules</strong> —
                        delivered the dental clearance Patient X needed to start cancer therapy on time, after conventional
                        laboratory work failed twice.
                    </p>
                    <div className="mt-7 inline-flex items-center gap-2 rounded-full bg-white/10 border border-white/15 px-4 py-2 text-[12px] text-slate-300" data-testid="cs-redaction-note">
                        <Lock size={12} className="text-cyan-300" /> Patient and clinic identities redacted for privacy — referred to as &ldquo;Patient X&rdquo; and &ldquo;the treating clinic&rdquo;.
                    </div>
                </div>
            </section>

            {/* PROLOGUE — the stakes */}
            <Chapter eyebrow="· Prologue · The stakes" title="A restoration that could not afford to fail">
                <p className="mt-5 text-[15.5px] text-slate-600 leading-[1.8]">
                    Patient X was diagnosed with cancer and required full dental clearance before oncological treatment could
                    begin. In an immunocompromised patient, an ill-fitting restoration is not a cosmetic problem — marginal gaps
                    trap food, harbour bacteria and open a direct path to infection at the exact moment the body can least fight
                    one. Every failed attempt burned days off a timeline measured against a life-critical therapy start date.
                </p>
            </Chapter>

            {/* CHAPTER 1 */}
            <section className="bg-slate-50 border-y border-slate-200">
                <div className="max-w-4xl mx-auto px-6 lg:px-10 py-14">
                    <div className="flex items-center gap-3">
                        <span className="w-10 h-10 rounded-2xl bg-rose-500/10 border border-rose-300 grid place-items-center"><AlertTriangle size={17} className="text-rose-500" /></span>
                        <div className="font-mono-tr text-[10.5px] tracking-[0.22em] uppercase text-rose-500">· Chapter 1 · The first failure</div>
                    </div>
                    <h2 className="mt-3 font-display text-3xl lg:text-4xl tracking-tight">A metal onlay with a gap you could see</h2>
                    <p className="mt-5 text-[15px] text-slate-600 leading-[1.8]">
                        The treating clinic first commissioned a traditional dental laboratory to cast a metal onlay. When it
                        arrived and was tried in, the problem was immediate and visible:
                    </p>
                    <Blockquote by="— The treating clinic, on the first metal onlay">
                        &ldquo;Huge gap has been observed in mesio-cervical margin region. Additionally, margins did not blend.&rdquo;
                    </Blockquote>
                    <p className="text-[15px] text-slate-600 leading-[1.8]">
                        The onlay failed to adapt to the prepared tooth on its mesial and cervical aspects. Food began lodging
                        between restoration and tooth — a breeding ground for bacteria, and for Patient X, an elevated infection
                        risk that could postpone or complicate cancer therapy. Interproximal contact with the neighbouring tooth
                        was never properly established.
                    </p>
                    <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="cs-gallery-failure">
                        <Figure src={IMG("metal-gap-highlight.jpg")} caption="The gap in the mesio-cervical margin region, as recorded intraorally by the treating clinic (highlighted)." />
                        <Figure src={IMG("metal-lingual.jpg")} caption="Lingual view — the cast metal onlay's margins failing to blend with the prepared tooth." />
                        <Figure src={IMG("metal-buccal.jpg")} caption="Buccal view of the failed metal onlay in situ." />
                        <Figure src={IMG("metal-margin-closeup.jpg")} caption="Close-up — the open margin that trapped food and threatened infection." />
                        <Figure src={IMG("fitting-side-view.jpg")} caption="Side view during clinical assessment of the metal restoration." />
                    </div>
                </div>
            </section>

            {/* CHAPTER 2 */}
            <Chapter eyebrow="· Chapter 2 · The remake that made it worse" title="The second onlay couldn't even be seated">
                <p className="mt-5 text-[15px] text-slate-600 leading-[1.8]">
                    With the therapy start date closing in, the clinic requested an urgent remake from the same laboratory.
                    The second casting arrived — and was worse than the first:
                </p>
                <Blockquote by="— The treating clinic, on the remake">
                    &ldquo;There is a huge marginal gap between restoration and the prepared tooth.&rdquo;
                </Blockquote>
                <p className="text-[15px] text-slate-600 leading-[1.8]">
                    This onlay was so grossly ill-fitting it could not be placed on the prepared tooth at all. Two consecutive
                    failures pointed to something systemic: casting shrinkage and distortion, impression or master-cast
                    inaccuracy, and the inherent limits of metal casting for high-precision work. A <strong>temporary onlay</strong> was
                    placed as an emergency measure — with a survival window of only <strong>10–15 days</strong>. The countdown to the
                    cancer treatment now had a second, shorter countdown inside it.
                </p>
                <div className="mt-8 grid sm:grid-cols-3 gap-4">
                    {[
                        { t: "Marginal gaps", d: "Metal shrinks and distorts as castings cool — precise adaptation is fundamentally hard to repeat." },
                        { t: "Corrosion & allergy", d: "Dental alloys can corrode and release metal ions; nickel is a common allergen — a real risk for an immunocompromised patient." },
                        { t: "Broken contacts", d: "Poor interproximal contact meant food impaction, gingival inflammation and mounting periodontal risk." },
                    ].map((c) => (
                        <div key={c.t} className="rounded-2xl border border-slate-200 bg-slate-50/40 p-5">
                            <div className="flex items-center gap-2 mb-2"><XCircle size={15} className="text-rose-500" /><span className="font-semibold text-[14px]">{c.t}</span></div>
                            <p className="text-[12.5px] text-slate-600 leading-relaxed">{c.d}</p>
                        </div>
                    ))}
                </div>
            </Chapter>

            {/* CHAPTER 3 — the turn */}
            <section className="bg-[#0B1120] text-white">
                <div className="max-w-4xl mx-auto px-6 lg:px-10 py-14">
                    <div className="flex items-center gap-3">
                        <span className="w-10 h-10 rounded-2xl bg-cyan-500/10 border border-cyan-400/30 grid place-items-center"><Microscope size={17} className="text-cyan-300" /></span>
                        <div className="font-mono-tr text-[10.5px] tracking-[0.22em] uppercase text-cyan-300/80">· Chapter 3 · The turn</div>
                    </div>
                    <h2 className="mt-3 font-display text-3xl lg:text-4xl tracking-tight">The clinic calls Toothrocket</h2>
                    <p className="mt-5 text-[15px] text-slate-300/90 leading-[1.8]">
                        The clinic sent the case to Toothrocket with one explicit request: <em>close the interproximal space
                        completely</em>. Our design team achieved the maximum possible closure within the onlay's fitting
                        constraints — and made three deliberate engineering decisions:
                    </p>
                    <div className="mt-7 grid sm:grid-cols-3 gap-4" data-testid="cs-design-decisions">
                        {[
                            { t: "0.5 mm — not 0.3 mm", d: "Thickness was deliberately increased above the standard because Patient X required high bite-force sustainability." },
                            { t: "Margins left for adjustment", d: "Built-in chairside adjustment capability — the design software blocks any export that would breach minimum thickness." },
                            { t: "Verified before printing", d: "In both the scanned file and the physical model, the bite sat correctly with no high points — checked before fabrication." },
                        ].map((c) => (
                            <div key={c.t} className="rounded-2xl bg-white/[0.06] border border-white/10 p-5">
                                <div className="text-[14px] font-semibold text-cyan-300">{c.t}</div>
                                <p className="mt-1.5 text-[12.5px] text-slate-400 leading-relaxed">{c.d}</p>
                            </div>
                        ))}
                    </div>
                    <p className="mt-6 text-[15px] text-slate-300/90 leading-[1.8]">
                        The nano-hybrid ceramic onlay was designed, 3D-printed and <strong className="text-white">delivered on 02-04-25</strong> —
                        seated by the clinic the very next day. Days, not weeks. Inside the temporary onlay's window.
                    </p>
                    <div className="mt-8 grid sm:grid-cols-3 gap-4" data-testid="cs-gallery-onlay">
                        {[
                            ["onlay-printed-1.jpg", "The nano-hybrid ceramic onlay, 3D-printed on its verification model."],
                            ["onlay-printed-2.jpg", "Side profile on the printed model — margins designed from the digital scan."],
                            ["onlay-model.jpg", "Fit verified on the printed model before dispatch."],
                        ].map(([f, cap]) => (
                            <figure key={f} className="rounded-2xl overflow-hidden border border-white/10 bg-white/[0.04]">
                                <a href={IMG(f)} target="_blank" rel="noreferrer">
                                    <img src={IMG(f)} alt={cap} loading="lazy" className="w-full h-64 object-cover hover:opacity-90 transition" />
                                </a>
                                <figcaption className="px-4 py-3 text-[12px] text-slate-400 leading-snug">{cap}</figcaption>
                            </figure>
                        ))}
                    </div>
                </div>
            </section>

            {/* CHAPTER 4 — chairside */}
            <Chapter eyebrow="· Chapter 4 · Chairside" title="One visit, two adjustments, a final fit">
                <p className="mt-5 text-[15px] text-slate-600 leading-[1.8]">
                    At try-in, the onlay presented occlusally high and the margins did not yet blend — precisely the scenario
                    the adjustment margins were designed for. The dentist reduced the marked high points on the onlay, then
                    discovered and reduced a high point on the <em>opposing</em> tooth that had been contributing to the bite
                    discrepancy all along. The onlay — printed thicker on purpose to survive exactly this kind of adjustment —
                    seated correctly and was cemented.
                </p>
                <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="cs-gallery-fit">
                    <Figure src={IMG("bite-vs-scan.jpg")} caption="Original bite vs the digital bite scan — verified sitting correctly before fabrication." />
                    <Figure src={IMG("high-points-intraoral.jpg")} caption="Articulating paper marks the occlusal high points intraorally." />
                    <Figure src={IMG("onlay-marked-points.jpg")} caption="The onlay with marked high points, ready for chairside reduction." />
                    <Figure src={IMG("onlay-adjusted.jpg")} caption="After reducing the high points — the onlay ready for final cementation." />
                </div>
            </Chapter>

            {/* DOUBLE BONDING */}
            <section className="max-w-5xl mx-auto px-6 lg:px-10 py-14" data-testid="cs-double-bonding">
                <div className="grid lg:grid-cols-2 gap-10 items-start">
                    <div>
                        <div className="font-mono-tr text-[10.5px] tracking-[0.22em] uppercase text-cyan-700">· The nanotech difference</div>
                        <h2 className="mt-2 font-display text-3xl lg:text-4xl tracking-tight leading-[1.15]">
                            Double bonding of molecules.<br />The market still bonds once.
                        </h2>
                        <p className="mt-5 text-[15px] text-slate-600 leading-[1.8]">
                            Conventional nano-ceramics on the market today rely on a <strong>single molecular bond</strong> between the
                            resin matrix and the ceramic filler particles. That single interface is exactly where restorations fail:
                            micro-gaps open under occlusal load, moisture creeps in, margins degrade.
                        </p>
                        <p className="mt-4 text-[15px] text-slate-600 leading-[1.8]">
                            Toothrocket's nano-hybrid ceramic is engineered with <strong>double bonding of molecules</strong> — each
                            nano-filler particle is coupled to the matrix twice, creating a densely cross-linked interface that
                            conventional single-bond materials simply cannot match. It is why the margin Patient X received is
                            the margin that was designed.
                        </p>
                    </div>
                    <div className="rounded-3xl border border-slate-200 bg-slate-50/40 p-7">
                        <div className="flex items-center gap-2.5 mb-5">
                            <Atom size={18} className="text-cyan-600" />
                            <div className="font-display text-lg">What double bonding delivers</div>
                        </div>
                        <ul className="space-y-3.5">
                            {[
                                "Denser matrix–filler interface — minimised polymerisation shrinkage, so the printed margin is the designed margin",
                                "Higher fracture and chipping resistance under normal and high occlusal load",
                                "Superior marginal integrity — the exact failure point of both metal casting and single-bond ceramics",
                                "Long-term colour and surface stability with zero metal-ion release",
                                "Biocompatible and metal-free — no nickel, no corrosion, safe for immunocompromised patients",
                            ].map((b) => (
                                <li key={b} className="flex items-start gap-2.5 text-[13.5px] text-slate-700 leading-relaxed">
                                    <CheckCircle2 size={15} className="text-emerald-600 mt-0.5 shrink-0" /> {b}
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            </section>

            {/* COMPARISON TABLE */}
            <section className="max-w-5xl mx-auto px-6 lg:px-10 pb-14">
                <div className="font-mono-tr text-[10.5px] tracking-[0.22em] uppercase text-cyan-700">· Head to head</div>
                <h2 className="mt-2 font-display text-3xl tracking-tight">Metal onlay vs Toothrocket nano-hybrid ceramic</h2>
                <div className="mt-7 rounded-2xl border border-slate-200 overflow-hidden overflow-x-auto" data-testid="cs-comparison-table">
                    <table className="w-full text-[13px] min-w-[640px]">
                        <thead>
                            <tr className="bg-[#0B1120] text-white">
                                <th className="text-left px-5 py-3.5 font-medium w-[22%]">Property</th>
                                <th className="text-left px-5 py-3.5 font-medium w-[36%]">Metal onlay</th>
                                <th className="text-left px-5 py-3.5 font-medium text-cyan-300">Nano-hybrid ceramic (double-bonded)</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                            {COMPARISON.map((r) => (
                                <tr key={r.prop} className="bg-white">
                                    <td className="px-5 py-3.5 font-semibold text-slate-900">{r.prop}</td>
                                    <td className="px-5 py-3.5 text-slate-500">{r.metal}</td>
                                    <td className="px-5 py-3.5 text-slate-800 bg-cyan-50/40">{r.nano}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </section>

            {/* EPILOGUE — outcome */}
            <section className="bg-slate-50 border-y border-slate-200">
                <div className="max-w-4xl mx-auto px-6 lg:px-10 py-16">
                    <div className="flex items-center gap-3">
                        <span className="w-10 h-10 rounded-2xl bg-emerald-500/10 border border-emerald-300 grid place-items-center"><HeartPulse size={17} className="text-emerald-600" /></span>
                        <div className="font-mono-tr text-[10.5px] tracking-[0.22em] uppercase text-emerald-600">· Epilogue · The outcome</div>
                    </div>
                    <h2 className="mt-3 font-display text-3xl lg:text-4xl tracking-tight">Seated, cleared, and on to treatment</h2>
                    <div className="mt-8 grid sm:grid-cols-3 gap-4" data-testid="cs-outcome-stats">
                        {[
                            { k: "2 → 0", v: "failed metal attempts before Toothrocket; zero remakes after" },
                            { k: "1 visit", v: "chairside adjustment — occlusal high points reduced and cemented same day" },
                            { k: "On time", v: "dental clearance achieved inside the 10–15 day temporary-onlay window" },
                        ].map((s) => (
                            <div key={s.k} className="rounded-2xl border border-slate-200 bg-white p-6">
                                <div className="font-display text-3xl text-cyan-700 tracking-tight">{s.k}</div>
                                <div className="mt-2 text-[13px] text-slate-600 leading-relaxed">{s.v}</div>
                            </div>
                        ))}
                    </div>
                    <p className="mt-8 text-[14.5px] text-slate-600 leading-[1.8]">
                        Patient X received a precise, biocompatible, metal-free restoration — and proceeded to cancer treatment
                        on schedule. Every Toothrocket restoration, including this one, ships with a live
                        <Link to="/warranty/TR-DEMO-2026" className="text-cyan-700 hover:underline font-medium"> ToothPassport™</Link> —
                        a 10-year clinical warranty valid anywhere in the world.
                    </p>
                </div>
            </section>

            {/* CTA */}
            <section className="bg-[#0B1120] text-white">
                <div className="max-w-4xl mx-auto px-6 lg:px-10 py-16 text-center">
                    <ShieldCheck size={28} className="mx-auto text-cyan-300 mb-4" />
                    <h2 className="font-display text-3xl lg:text-4xl tracking-tight">Have a case that can&rsquo;t afford a remake?</h2>
                    <p className="mt-4 text-white/70 max-w-xl mx-auto">Send it to the lab that fits medically complex patients first time — with double-bonded nano-hybrid ceramic.</p>
                    <div className="mt-7 flex flex-wrap justify-center gap-3">
                        <Link to="/login?signup=1" data-testid="cs-cta-send-case" className="px-6 py-3.5 rounded-xl bg-cyan-400 text-[#0B1120] text-[14px] font-medium hover:bg-cyan-300 inline-flex items-center gap-2">
                            Send a case <ArrowRight size={15} />
                        </Link>
                        <BookDemoButton testid="cs-cta-demo" className="px-6 py-3.5 rounded-xl border border-white/20 text-white text-[14px] font-medium hover:bg-white/10 inline-flex items-center gap-2" />
                    </div>
                </div>
            </section>
        </div>
    );
}
